/**
 * Kept, in the Journal (mystic page 2026-10-29, `Claude outputs/mystic-page-
 * 2026-10-29/MIRROR-OUTLINE.md` "Kept, in the Journal"; mocks 23/24).
 *
 * Things from others that spoke to the seeker (a Mystic's drop, a lesson
 * picture, a card), kept with Keep. The shelf is the day page's own frame and
 * each kept thing is the Journal's built taped-photo moment (JournalMoment,
 * kind 'msg'): the date in the margin, the giver's mark (square for a Mystic,
 * round for a seeker), the line and the taped picture. Newest first.
 *
 * Also the "You & {name}" cards: the kept Our Stars reports (moved here from
 * the Mirror). This is the one place the Kept payload is mapped.
 */
import { shortDate, dateKey } from '@/utils/journal-book'
import { youAndThem } from '@/utils/you-and-them'

const NUM = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten']
const str = (x) => (x === null || x === undefined ? '' : String(x))

/** "2026-10-29" from a date key or an ISO time (local), else ''. */
function keyOf (date, iso) {
  if (/^\d{4}-\d{2}-\d{2}$/.test(str(date))) return str(date)
  const t = iso ? new Date(iso) : null
  return t && !isNaN(t) ? dateKey(t) : ''
}

/** The giver (API.md §6 `giver`): a Mystic (square mark) or a seeker (round). */
export function giverOf (g) {
  const f = g && typeof g === 'object' ? g : {}
  const name = str(f.name)
  const avatar = str(f.portrait || f.avatar)
  return f.kind === 'seeker'
    ? { mystic: null, from: { name, avatar } }
    : { mystic: { name: name || 'Mystic Aida', sub: str(f.sub_domain || f.sub), avatar }, from: null }
}

/** The shelf's line when the server sends none (API.md §6, without the title). */
function lineFor (source, name, isSeeker) {
  const who = name || (isSeeker ? 'a seeker' : 'Mystic Aida')
  if (source === 'lesson') return `You kept a picture from ${who}’s lesson`
  if (source === 'painting') return `You kept ${who}’s painting`
  if (source === 'card') return `You kept ${who}’s card`
  if (source === 'drop') return `You kept ${who}’s drop`
  return `You kept ${who}’s picture`
}

function normalizeItem (it, i) {
  if (!it || typeof it !== 'object') return null
  const key = keyOf(it.date, it.kept_at || it.at)
  if (!key) return null
  const g = giverOf(it.giver)
  const name = (g.mystic || g.from).name
  const id = str(it.id || `${key}-${i}`)
  return {
    id, key, at: str(it.kept_at || it.at), source: str(it.source),
    moment: {
      id, kind: 'msg', type: 'image_kept', time: shortDate(key), hidden: false,
      title: str(it.title), text: str(it.text || it.caption), image_url: str(it.image_url),
      mystic: g.mystic, from: g.from, to: null, who: null, card: null, cards: [],
      line: str(it.line) || lineFor(str(it.source), name, !!g.from),
    },
  }
}

/** journal/kept/ → { count, line, items (newest first), thumbs (the card's three small pictures) }. */
export function normalizeKept (raw) {
  const list = raw && Array.isArray(raw.items) ? raw.items : []
  const items = list.map(normalizeItem).filter(Boolean)
    .sort((a, b) => (a.key === b.key ? (b.at > a.at ? 1 : b.at < a.at ? -1 : 0) : a.key < b.key ? 1 : -1))
  const count = Math.max(items.length, Number(raw && raw.count) || 0)
  const preview = raw && Array.isArray(raw.preview) ? raw.preview.filter(Boolean).map(String) : []
  return { count, items, thumbs: preview.length ? preview.slice(0, 3) : thumbsOf(items), line: str(raw && raw.line) }
}
export const thumbsOf = (items) => items.map((x) => x.moment.image_url).filter(Boolean).slice(0, 3)

/** The contents card's line: "5 things that spoke to you". */
export const keptCountLine = (n) => `${n} ${n === 1 ? 'thing' : 'things'} that spoke to you`

/** The shelf's rule: "Five things · newest first" (one thing needs no order). */
export function keptRule (n) {
  if (n === 1) return 'One thing'
  return `${NUM[n] || n} things · newest first`
}

/** The kept Our Stars reports (journal/kept/ `you_and`, the Mirror's old `our_stars` shape): You & {name}. */
export function normalizeOurStars (list) {
  return (Array.isArray(list) ? list : [])
    .filter((o) => o && typeof o === 'object' && o.with_name && (o.report || (o.you_and && o.you_and.summary)))
    .map((o) => {
      // The new words (recipient polish §6, BE contract you_and): the summary
      // and the three lines, as the gift room and the Community show them.
      const v = o.you_and ? youAndThem({ you_and: o.you_and }, { other: str(o.with_name) }) : null
      return {
        id: o.id, name: str(o.with_name), report: (v && v.summary) || str(o.report), key: keyOf(o.date, o.created_at || o.at),
        lines: v ? v.threads.map((t) => ({ n: t.n, heading: t.heading, sentence: t.sentence, picture: t.picture, tryThis: t.tryThis })) : [],
      }
    })
}

/** "Your stars, side by side · Oct 12". */
export const starsMeta = (o) => `Your stars, side by side${o && o.key ? ` · ${shortDate(o.key)}` : ''}`
